// ─────────────────────────────────────────────────────────────
//  src/pages/ClientJobDetail.js
//  CLIENT JOB DETAIL — Full-page expanded view of a single order
//  Route: /client/orders/:jobId
//  Triggered by "📋 View Full Order Details →" button in ClientPortal
// ─────────────────────────────────────────────────────────────

import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import JobMessages from '../components/JobMessages'
import toast from 'react-hot-toast'

// ── CONSTANTS ─────────────────────────────────────────────────
const STRIPE_PAYMENT_LINK = 'https://buy.stripe.com/8x2cN5esR2NTcEM29DgA804'

// ── STATUS CONFIG (matches ClientPortal exactly) ──────────────
const STATUS_CONFIG = {
  new:                  { label:'Order Received',              color:'#7C6A2E', bg:'#EEF0F8', border:'#4D4E8D', dot:'#4D4E8D' },
  awaiting_assignment:  { label:'Finding Your Notary',         color:'#7C6A2E', bg:'#EEF0F8', border:'#4D4E8D', dot:'#F59E0B' },
  pending_acceptance:   { label:'Notary Notified',             color:'#1A4A6B', bg:'#EBF4FF', border:'#3B82F6', dot:'#F59E0B' },
  assigned:             { label:'Notary Notified',             color:'#1A4A6B', bg:'#EBF4FF', border:'#3B82F6', dot:'#3B82F6' },
  confirmed:            { label:'Appointment Confirmed',       color:'#1A4A2E', bg:'#EAF2EE', border:'#5E8A72', dot:'#5E8A72' },
  in_progress:          { label:'Signing In Progress',         color:'#3A3D40', bg:'#EBF4FF', border:'#185FA5', dot:'#185FA5' },
  scanbacks_uploaded:   { label:'Documents Uploaded',          color:'#1A4A3E', bg:'#E8F5F0', border:'#2D7A6A', dot:'#2D7A6A' },
  completed:            { label:'Completed by Notary',         color:'#1A4A2E', bg:'#EAF2EE', border:'#5E8A72', dot:'#5BA882' },
  client_approved:      { label:'Final Review Complete',       color:'#3A1A5E', bg:'#F0EBF8', border:'#6A3A9A', dot:'#6A3A9A' },
  payment_received:     { label:'Order Complete',              color:'#1A4A2E', bg:'#EAF2EE', border:'#5E8A72', dot:'#5E8A72' },
  notary_paid:          { label:'Order Complete',              color:'#1A4A2E', bg:'#EAF2EE', border:'#5E8A72', dot:'#5E8A72' },
  awaiting_review:      { label:'Awaiting Final Review',       color:'#3A1A5E', bg:'#F0EBF8', border:'#6A3A9A', dot:'#6A3A9A' },
  paid:                 { label:'Invoice Paid ✓',              color:'#3A3D40', bg:'#EBF4FF', border:'#151715', dot:'#5BA882' },
  closed:               { label:'Closed',                      color:'#2A2A2A', bg:'#F4F4F4', border:'#8A8A8A', dot:'#8A8A8A' },
  cancelled:            { label:'Cancelled',                   color:'#6B2020', bg:'#FDF2F2', border:'#E24B4A', dot:'#E24B4A' },
}

// ── LUXURY STYLES ─────────────────────────────────────────────
const DETAIL_STYLES = `
  :focus-visible { outline:2px solid #4D4E8D; outline-offset:3px; border-radius:4px; }
  .lca-skip { position:absolute; top:-100px; left:16px; background:#4D4E8D; color:#fff; padding:8px 16px; border-radius:8px; font-size:13px; font-weight:600; text-decoration:none; z-index:9999; transition:top .2s; }
  .lca-skip:focus { top:16px; }
  .detail-card { background:#fff; border:1px solid #E2E4E7; border-radius:14px; overflow:hidden; margin-bottom:16px; }
  .detail-card-header { padding:14px 20px; background:linear-gradient(135deg,#4D4E8D,#41427a); color:#fff; font-family:'Plus Jakarta Sans',sans-serif; font-size:18px; font-weight:600; letter-spacing:0.04em; }
  .detail-grid-2 { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
  @media(max-width:760px){ .detail-grid-2 { grid-template-columns:1fr; } }
  .detail-info-row { display:grid; grid-template-columns:repeat(3,1fr); gap:0; }
  @media(max-width:600px){ .detail-info-row { grid-template-columns:1fr 1fr; } }
  .detail-info-cell { padding:12px 16px; border-right:1px solid #EEEFF1; border-bottom:1px solid #EEEFF1; }
  .detail-info-cell:last-child { border-right:none; }
  .detail-label { font-size:10px; color:#A2A6AA; letter-spacing:0.08em; font-weight:600; margin-bottom:4px; text-transform:uppercase; }
  .detail-value { font-size:14px; color:#151715; font-weight:500; }
  .lux-tracker { display:flex; flex-direction:column; gap:10px; padding:18px 20px; }
  .lux-tracker-step { display:flex; align-items:center; gap:12px; }
  .lux-tracker-dot { width:28px; height:28px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:12px; font-weight:600; flex-shrink:0; }
  .lux-tracker-line { width:2px; height:18px; margin-left:13px; }
  .lux-vetted-badge { display:inline-flex; align-items:center; gap:5px; background:linear-gradient(135deg,#4D4E8D,#41427a); border:1px solid rgba(77,78,141,0.3); border-radius:20px; padding:3px 10px; font-size:10px; font-weight:600; color:#fff; letter-spacing:0.04em; }
  .lux-notary-card { background:linear-gradient(135deg,#4D4E8D,#41427a); border-radius:14px; padding:16px; display:flex; align-items:center; gap:14px; }
  .back-btn { display:inline-flex; align-items:center; gap:6px; background:none; border:none; color:#41427a; font-size:13px; font-weight:600; cursor:pointer; padding:8px 0; margin-bottom:8px; }
  .back-btn:hover { color:#151715; }
  .qc-pending-banner { background:#EEF0F8; border:2px solid #4D4E8D; border-radius:12px; padding:14px 18px; margin-bottom:16px; }
  .pay-banner { background:linear-gradient(135deg,#635BFF,#4F46E5); border-radius:14px; padding:18px 22px; margin-bottom:16px; color:#fff; display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:12px; }
`

// ── BACK BUTTON ───────────────────────────────────────────────
function BackToOrders() {
  const navigate = useNavigate()
  return (
    <button type="button" className="back-btn" onClick={() => navigate('/client/orders')}>
      ← Back to My Orders
    </button>
  )
}

// ── STATUS BADGE (large version) ──────────────────────────────
function StatusBadgeLarge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.new
  return (
    <div style={{ display:'inline-flex', alignItems:'center', gap:10, background:cfg.bg, border:`2px solid ${cfg.border}40`, borderRadius:24, padding:'8px 18px' }}>
      <div style={{ width:10, height:10, borderRadius:'50%', background:cfg.dot, boxShadow:`0 0 8px ${cfg.dot}` }} />
      <span style={{ fontSize:14, fontWeight:700, color:cfg.color, letterSpacing:'0.04em' }}>{cfg.label}</span>
    </div>
  )
}

// ── STATUS TIMELINE (full expanded version) ───────────────────
function FullStatusTimeline({ status }) {
  const displaySteps = [
    { key:'new',                label:'Order Received'     },
    { key:'assigned',           label:'Notary Notified'    },
    { key:'accepted',           label:'Notary Confirmed'   },
    { key:'arrived',            label:'Notary Arrived'     },
    { key:'completed',          label:'Signing Complete'   },
    { key:'scanbacks_uploaded', label:'Documents Uploaded' },
    { key:'client_approved',    label:'Final Review'       },
    { key:'closed',             label:'Order Complete'     },
  ]
  // Same canonical mapping the client "My Orders" tracker uses — keeps every portal in sync
  const STEP_FOR_STATUS = {
    new:'new', awaiting_assignment:'new',
    assigned:'assigned', declined:'assigned',
    accepted:'accepted', on_the_way:'accepted',
    arrived:'arrived',
    completed:'completed',
    scanbacks_uploaded:'scanbacks_uploaded',
    client_approved:'client_approved', payment_received:'closed', notary_paid:'closed', closed:'closed',
  }
  const displayIdx = displaySteps.findIndex(s => s.key === (STEP_FOR_STATUS[status] || 'new'))
  return (
    <div className="lux-tracker">
      {displaySteps.map((s, i) => {
        const done = i < displayIdx, current = i === displayIdx
        return (
          <div key={s.key}>
            <div className="lux-tracker-step">
              <div className="lux-tracker-dot" style={{ background:done?'#5E8A72':current?'#4D4E8D':'#E2E4E7', color:done||current?'#fff':'#A2A6AA' }}>
                {done ? '✓' : i+1}
              </div>
              <span style={{ fontSize:14, color:current?'#151715':done?'#5E8A72':'#A2A6AA', fontWeight:current?700:500 }}>
                {s.label}{current && <span style={{ fontSize:11, color:'#4D4E8D', marginLeft:10, fontWeight:600 }}>← Current Step</span>}
              </span>
            </div>
            {i < displaySteps.length-1 && <div className="lux-tracker-line" style={{ background:done?'#5E8A72':'#E2E4E7' }} />}
          </div>
        )
      })}
    </div>
  )
}

// ── LIVE PACKAGE TRACKING ─────────────────────────────────────
function LiveTrackingFull({ trackingNumber, carrier: carrierProp }) {
  if (!trackingNumber) return null
  const isUPS  = /^1Z/.test(trackingNumber)
  const isUSPS = /^(94|93|92|95)\d{20}$/.test(trackingNumber.replace(/\s/g,''))
  const carrier  = carrierProp || (isUPS ? 'UPS' : isUSPS ? 'USPS' : 'FedEx')
  const trackUrl = carrier === 'UPS'
    ? `https://www.ups.com/track?tracknum=${trackingNumber}`
    : carrier === 'USPS'
    ? `https://tools.usps.com/go/TrackConfirmAction?qtc_tLabels1=${trackingNumber}`
    : `https://www.fedex.com/fedextrack/?trknbr=${trackingNumber}`
  const colors = {
    UPS:   { bg:'#351C15', color:'#FFB500', icon:'📦' },
    FedEx: { bg:'#4D148C', color:'#FF6200', icon:'📫' },
    USPS:  { bg:'#004B97', color:'#fff',    icon:'✉️'  },
  }
  const c = colors[carrier]
  return (
    <div className="detail-card">
      <div className="detail-card-header">📦 Live Package Tracking</div>
      <div style={{ padding:'16px 20px', background:c.bg, display:'flex', alignItems:'center', gap:14, flexWrap:'wrap' }}>
        <span style={{ fontSize:32 }}>{c.icon}</span>
        <div style={{ flex:1, minWidth:160 }}>
          <div style={{ fontSize:11, color:'rgba(255,255,255,0.5)', letterSpacing:'0.08em', marginBottom:4 }}>{carrier} TRACKING NUMBER</div>
          <div style={{ fontSize:16, fontWeight:700, color:c.color, letterSpacing:'0.04em' }}>{trackingNumber}</div>
        </div>
        <a href={trackUrl} target="_blank" rel="noreferrer"
          style={{ background:c.color, color:c.bg, fontSize:13, fontWeight:700, padding:'10px 20px', borderRadius:10, textDecoration:'none', whiteSpace:'nowrap' }}>
          Track Package →
        </a>
      </div>
    </div>
  )
}

// ── NOTARY PROFILE CARD ───────────────────────────────────────
function NotaryProfileCardFull({ notaryId }) {
  const [notary, setNotary] = useState(null)
  useEffect(() => {
    if (!notaryId) return
    supabase.from('notaries').select('*, profiles(full_name, phone, email, bio, headshot_url)').eq('id', notaryId).single().then(({data}) => setNotary(data))
  }, [notaryId])
  if (!notaryId) return (
    <div className="detail-card">
      <div className="detail-card-header">👤 Your Notary</div>
      <div style={{ padding:'30px 20px', textAlign:'center', color:'#A2A6AA', fontSize:13 }}>
        ⏳ Your notary will appear here once assigned!!
      </div>
    </div>
  )
  if (!notary) return (
    <div className="detail-card">
      <div className="detail-card-header">👤 Your Notary</div>
      <div style={{ padding:'30px 20px', textAlign:'center', color:'#A2A6AA', fontSize:13 }}>Loading notary...</div>
    </div>
  )
  const name     = notary.profiles?.full_name || 'Your NSV Notary'
  const phone    = notary.profiles?.phone
  const email    = notary.profiles?.email
  const bio      = notary.profiles?.bio
  const headshot = notary.profiles?.headshot_url
  const tags = []
  if (notary.commission_state) tags.push(`📜 ${notary.commission_state}`)
  if (notary.years_experience) tags.push(`⭐ ${notary.years_experience} yrs`)
  if (notary.ron_certified) tags.push('💻 RON Certified')
  if (notary.languages?.filter(l => l !== 'English').length > 0) tags.push('🗣️ Bilingual')
  return (
    <div className="detail-card">
      <div className="detail-card-header">👤 Your Notary</div>
      <div style={{ padding:'16px 20px' }}>
        <div className="lux-notary-card">
          <div style={{ width:64, height:64, borderRadius:'50%', overflow:'hidden', background:'rgba(77,78,141,0.15)', border:'2px solid rgba(77,78,141,0.3)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
            {headshot ? <img src={headshot} alt={name} style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : <span style={{ fontSize:28 }}>🧑‍💼</span>}
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap', marginBottom:4 }}>
              <div style={{ fontFamily:'Plus Jakarta Sans,sans-serif', fontSize:19, color:'#fff', fontWeight:600 }}>{name}</div>
              {notary.is_founder && <span style={{ background:'linear-gradient(135deg,#41427a,#4D4E8D)', color:'#fff', fontSize:9, fontWeight:700, padding:'2px 8px', borderRadius:20 }}>🏅 FOUNDER</span>}
              {notary.is_ron && <span style={{ background:'linear-gradient(135deg,#1A4A6B,#2D7AB5)', color:'#fff', fontSize:9, fontWeight:700, padding:'2px 8px', borderRadius:20 }}>💻 RON</span>}
            </div>
            {notary.is_vetted && <div className="lux-vetted-badge" style={{ marginBottom:6 }}>★ NSV Vetted</div>}
            {notary.avg_rating > 0 && <div style={{ fontSize:12, color:'#fff', marginBottom:6 }}>{"★".repeat(Math.round(notary.avg_rating))} {notary.avg_rating}/5</div>}
            {bio && <div style={{ fontSize:11, color:'rgba(255,255,255,0.6)', lineHeight:1.5, marginBottom:8 }}>{bio}</div>}
            {tags.length > 0 && (
              <div style={{ display:'flex', flexWrap:'wrap', gap:4, marginBottom:8 }}>
                {tags.map((t,i) => <span key={i} style={{ fontSize:10, background:'rgba(255,255,255,0.1)', color:'rgba(255,255,255,0.7)', padding:'3px 9px', borderRadius:20 }}>{t}</span>)}
              </div>
            )}
            <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
              {phone && <a href={`tel:${phone}`} style={{ fontSize:12, color:'#4D4E8D', textDecoration:'none', fontWeight:600 }}>📞 {phone}</a>}
              {email && <a href={`mailto:${email}`} style={{ fontSize:12, color:'#4D4E8D', textDecoration:'none', fontWeight:600 }}>📧 Email</a>}
            </div>
          </div>
          {notary.vendor_number && (
            <div style={{ textAlign:'right', flexShrink:0 }}>
              <div style={{ fontSize:10, color:'rgba(255,255,255,0.35)', marginBottom:4 }}>Vendor</div>
              <div style={{ fontFamily:'Plus Jakarta Sans,sans-serif', fontSize:14, color:'#fff', fontWeight:600 }}>{notary.vendor_number}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ── STAR RATING (internal) ────────────────────────────────────
function StarRatingFull({ jobId, notaryId, isCompleted }) {
  const [rating,    setRating]    = useState(0)
  const [hover,     setHover]     = useState(0)
  const [comment,   setComment]   = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [saving,    setSaving]    = useState(false)

  useEffect(() => {
    supabase.from('jobs').select('client_rating, client_rating_comment').eq('id', jobId).single()
      .then(({ data }) => {
        if (data?.client_rating) {
          setRating(data.client_rating)
          setComment(data.client_rating_comment || '')
          setSubmitted(true)
        }
      })
  }, [jobId])

  async function submitRating() {
    if (!rating) { toast.error('Please select a star rating!!'); return }
    setSaving(true)
    try {
      await supabase.from('jobs').update({
        client_rating:         rating,
        client_rating_comment: comment || null,
        client_rated_at:       new Date().toISOString(),
      }).eq('id', jobId)
      const { data: jobs } = await supabase.from('jobs')
        .select('client_rating').eq('notary_id', notaryId).not('client_rating', 'is', null)
      if (jobs?.length) {
        const avg = Math.round((jobs.reduce((a,j) => a + j.client_rating, 0) / jobs.length) * 10) / 10
        await supabase.from('notaries').update({ avg_rating: avg }).eq('id', notaryId)
      }
      setSubmitted(true)
      toast.success('Thank you for rating your notary!! 🌟 This drives their ranks & rewards!!')
    } catch (err) { toast.error(err.message) }
    finally { setSaving(false) }
  }

  if (!isCompleted) return null

  return (
    <div className="detail-card">
      <div className="detail-card-header">⭐ Rate Your Notary</div>
      {submitted ? (
        <div style={{ padding:'20px', textAlign:'center', background:'#FAFDF5' }}>
          <div style={{ fontSize:28, marginBottom:6 }}>{'⭐'.repeat(rating)}</div>
          <div style={{ fontSize:13, color:'#5E8A72', fontWeight:700 }}>Thank you for rating your notary!!</div>
          {comment && <div style={{ fontSize:12, color:'#5C6065', marginTop:6, fontStyle:'italic' }}>"{comment}"</div>}
          <div style={{ fontSize:11, color:'#A2A6AA', marginTop:8 }}>⭐ Loved your service?? Leave us a public review:<br/>
            <a href="https://g.page/r/loanclosingagent/review" target="_blank" rel="noreferrer" style={{ color:'#41427a', fontWeight:600, marginRight:12 }}>🌟 Google</a>
            <a href="https://www.yelp.com/biz/loan-closing-agent" target="_blank" rel="noreferrer" style={{ color:'#41427a', fontWeight:600 }}>⭐ Yelp</a>
          </div>
        </div>
      ) : (
        <div style={{ padding:'20px', background:'#FAFDF5' }}>
          <div style={{ fontSize:13, color:'#5C6065', marginBottom:12 }}>Your feedback drives notary ranks &amp; rewards in the NSV system!!</div>
          <div style={{ display:'flex', gap:8, marginBottom:12, justifyContent:'center' }}>
            {[1,2,3,4,5].map(s => (
              <button key={s} type="button"
                onMouseEnter={() => setHover(s)}
                onMouseLeave={() => setHover(0)}
                onClick={() => setRating(s)}
                aria-label={`Rate ${s} star${s > 1 ? 's' : ''}`}
                style={{ background:'none', border:'none', cursor:'pointer', fontSize:36, color: s <= (hover||rating) ? '#4D4E8D' : '#E2E4E7', transition:'color 0.15s', padding:2 }}
              >★</button>
            ))}
          </div>
          {rating > 0 && <div style={{ textAlign:'center', fontSize:13, color:'#5C6065', marginBottom:10 }}>{['','Poor','Fair','Good','Great','Excellent!!'][rating]}</div>}
          <textarea
            value={comment}
            onChange={e => setComment(e.target.value)}
            placeholder="Tell us about your experience (optional)..."
            rows={3}
            style={{ width:'100%', padding:'10px 14px', border:'1.5px solid #D7DADE', borderRadius:8, fontSize:13, resize:'vertical', marginBottom:12, fontFamily:'inherit' }}
          />
          <button onClick={submitRating} disabled={saving || !rating}
            style={{ width:'100%', background:'linear-gradient(135deg,#4D4E8D,#41427a)', color:'#fff', border:'none', borderRadius:10, padding:'12px 20px', fontSize:14, fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}>
            {saving ? 'Submitting...' : '⭐ Submit Rating'}
          </button>
        </div>
      )}
    </div>
  )
}

// ── DOCUMENT VAULT (closing pkg + scan-back QC) ───────────────
function DocumentVaultFull({ jobId, jobStatus }) {
  const { user } = useAuth()
  const [docs,      setDocs]      = useState([])
  const [loading,   setLoading]   = useState(true)
  const [uploading, setUploading] = useState(false)

  useEffect(() => {
    setLoading(true)
    supabase.from('job_documents').select('*').eq('job_id', jobId)
      .order('created_at',{ascending:false})
      .then(({data}) => { setDocs(data||[]); setLoading(false) })
  }, [jobId])

  function refresh() {
    supabase.from('job_documents').select('*').eq('job_id', jobId)
      .order('created_at',{ascending:false})
      .then(({data}) => setDocs(data||[]))
  }

  async function uploadPackage(e) {
    const file = e.target.files[0]
    if (!file) return
    setUploading(true)
    try {
      const ext  = file.name.split('.').pop()
      const path = `${jobId}/closing-package-${Date.now()}.${ext}`
      const { error } = await supabase.storage.from('job-documents').upload(path, file, { upsert: true })
      if (error) throw error
      const { data: { publicUrl } } = supabase.storage.from('job-documents').getPublicUrl(path)
      await supabase.from('job_documents').insert({
        job_id: jobId, uploaded_by: user?.id, role: 'client',
        doc_type: 'signing_package', file_name: file.name,
        file_url: publicUrl, file_size: file.size, qc_status: 'approved',
      })
      await supabase.from('jobs').update({ closing_package_uploaded_at: new Date().toISOString() }).eq('id', jobId)
      toast.success('📦 Closing package uploaded!! Your notary will be notified!!')
      e.target.value = ''
      refresh()
    } catch (err) { toast.error('Upload failed: ' + err.message) }
    finally { setUploading(false) }
  }

  async function approveDoc(docId) {
    try {
      await supabase.from('job_documents').update({
        qc_status: 'approved',
        qc_approved_at: new Date().toISOString(),
        qc_approved_by: user?.id,
      }).eq('id', docId)

      // Are any scan-backs still awaiting review (pending or needing corrections) after this one?
      const stillPending = docs.some(d =>
        d.id !== docId &&
        (d.doc_type === 'scan_back' || d.doc_type === 'completed_docs') &&
        (d.qc_status === 'pending' || d.qc_status === 'rejected')
      )

      // ✅ Always record the QC flag. Only flip the job to client_approved (which unlocks
      // the notary's shipping/tracking/receipt steps) once EVERY scan-back is approved —
      // and never downgrade a job that's already further along.
      const PAST = ['payment_received', 'notary_paid', 'closed']
      const jobUpdate = { title_qc_status: 'approved' }
      if (!stillPending && !PAST.includes(jobStatus)) jobUpdate.status = 'client_approved'
      await supabase.from('jobs').update(jobUpdate).eq('id', jobId)

      toast.success(stillPending
        ? '✅ Scan-back approved!! Approve the remaining scan-backs to clear your notary to ship!!'
        : '✅ All scan-backs approved!! Your notary is now cleared to ship!!')
      refresh()
    } catch (err) { toast.error(err.message) }
  }

  async function rejectDoc(docId) {
    const notes = window.prompt('Please describe what corrections are needed:')
    if (!notes) return
    try {
      await supabase.from('job_documents').update({
        qc_status: 'rejected',
        qc_rejected_at: new Date().toISOString(),
        qc_rejected_by: user?.id,
        qc_correction_notes: notes,
      }).eq('id', docId)
      await supabase.from('jobs').update({ title_qc_status: 'corrections_needed', title_qc_notes: notes }).eq('id', jobId)
      toast.success('📋 Correction request sent to notary!!')
      refresh()
    } catch (err) { toast.error(err.message) }
  }

  const signingPkg   = docs.filter(d => d.doc_type === 'signing_package')
  const scanBacks    = docs.filter(d => (d.doc_type === 'scan_back' || d.doc_type === 'completed_docs') && d.qc_status === 'approved')
  const pendingDocs  = docs.filter(d => (d.doc_type === 'scan_back' || d.doc_type === 'completed_docs') && d.qc_status === 'pending')
  const rejectedDocs = docs.filter(d => (d.doc_type === 'scan_back' || d.doc_type === 'completed_docs') && d.qc_status === 'rejected')

  return (
    <div className="detail-card">
      <div className="detail-card-header">📁 Document Vault</div>
      <div style={{ padding:'18px 20px' }}>
        {loading && <div style={{ fontSize:13, color:'#A2A6AA', textAlign:'center', padding:'20px 0' }}>Loading documents...</div>}

        {/* CLOSING PACKAGE UPLOAD */}
        {!loading && (
          <div style={{ background:'#EBF4FF', border:'1px solid rgba(24,95,165,0.2)', borderRadius:10, padding:'14px 16px', marginBottom:14 }}>
            <div style={{ fontSize:12, fontWeight:700, color:'#185FA5', marginBottom:8 }}>📦 CLOSING PACKAGE</div>
            {signingPkg.length > 0 ? (
              <div>
                {signingPkg.map(d => (
                  <div key={d.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', fontSize:13, marginBottom:6 }}>
                    <span style={{ color:'#151715' }}>✅ {d.file_name}</span>
                    <span style={{ fontSize:11, color:'#5E8A72' }}>Uploaded {new Date(d.created_at).toLocaleString()}</span>
                  </div>
                ))}
                <label style={{ cursor:'pointer', marginTop:6, display:'inline-block' }}>
                  <input type="file" accept=".pdf,.zip" style={{ display:'none' }} onChange={uploadPackage} disabled={uploading} />
                  <span style={{ fontSize:11, color:'#185FA5', textDecoration:'underline', cursor:'pointer' }}>{uploading ? 'Uploading...' : '↺ Upload new version'}</span>
                </label>
              </div>
            ) : (
              <div>
                <div style={{ fontSize:12, color:'#5C6065', marginBottom:10 }}>Upload the loan signing package for your notary to download and print!!</div>
                <label style={{ cursor: uploading ? 'wait' : 'pointer' }}>
                  <input type="file" accept=".pdf,.zip" style={{ display:'none' }} onChange={uploadPackage} disabled={uploading} />
                  <span style={{ display:'inline-block', background:'#185FA5', color:'#fff', fontSize:12, fontWeight:700, padding:'10px 18px', borderRadius:8, cursor:'pointer' }}>
                    {uploading ? '⏳ Uploading...' : '📦 Upload Closing Package'}
                  </span>
                </label>
              </div>
            )}
          </div>
        )}

        {/* PENDING — needs Title QC */}
        {!loading && pendingDocs.length > 0 && (
          <div className="qc-pending-banner">
            <div style={{ fontSize:13, fontWeight:700, color:'#41427a', marginBottom:10 }}>⏳ AWAITING YOUR REVIEW — Please approve or request corrections!!</div>
            {pendingDocs.map(d => (
              <div key={d.id} style={{ background:'#fff', borderRadius:8, padding:'12px 14px', marginBottom:8, border:'1px solid #EEEFF1' }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10, gap:10 }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:13, fontWeight:600, color:'#151715' }}>📄 {d.file_name}</div>
                    <div style={{ fontSize:11, color:'#A2A6AA' }}>{new Date(d.created_at).toLocaleString()}</div>
                  </div>
                  <a href={d.file_url} target="_blank" rel="noreferrer"
                    style={{ background:'#fff', border:'1.5px solid #4D4E8D', color:'#41427a', fontSize:11, fontWeight:600, padding:'7px 14px', borderRadius:7, textDecoration:'none' }}>
                    👁️ View
                  </a>
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  <button onClick={() => approveDoc(d.id)}
                    style={{ flex:1, background:'#5E8A72', color:'#fff', border:'none', borderRadius:8, padding:'10px 14px', fontSize:12, fontWeight:700, cursor:'pointer' }}>
                    ✅ Approve — Good to Ship!!
                  </button>
                  <button onClick={() => rejectDoc(d.id)}
                    style={{ flex:1, background:'#fff', color:'#E24B4A', border:'1.5px solid #E24B4A', borderRadius:8, padding:'10px 14px', fontSize:12, fontWeight:700, cursor:'pointer' }}>
                    ❌ Request Corrections
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* APPROVED scan-backs */}
        {!loading && scanBacks.length > 0 && (
          <div style={{ marginBottom:10 }}>
            <div style={{ fontSize:12, fontWeight:700, color:'#151715', letterSpacing:'0.06em', marginBottom:8 }}>✅ APPROVED SCAN-BACKS</div>
            {scanBacks.map(d => (
              <div key={d.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 0', borderBottom:'1px solid #EEEFF1' }}>
                <div>
                  <div style={{ fontSize:13, color:'#151715', fontWeight:500 }}>📄 {d.file_name}</div>
                  <div style={{ fontSize:11, color:'#5E8A72', fontWeight:500 }}>✅ Approved {d.qc_approved_at ? new Date(d.qc_approved_at).toLocaleString() : ''}</div>
                </div>
                <a href={d.file_url} target="_blank" rel="noreferrer"
                  style={{ background:'#4D4E8D', color:'#fff', fontSize:12, fontWeight:700, padding:'8px 16px', borderRadius:8, textDecoration:'none' }}>
                  ⬇️ Download
                </a>
              </div>
            ))}
          </div>
        )}

        {/* REJECTED — corrections requested */}
        {!loading && rejectedDocs.length > 0 && (
          <div style={{ background:'#FDF2F2', border:'1px solid rgba(226,75,74,0.2)', borderRadius:10, padding:'12px 14px', marginBottom:10 }}>
            <div style={{ fontSize:12, fontWeight:700, color:'#C0392B', marginBottom:8 }}>❌ CORRECTIONS REQUESTED</div>
            {rejectedDocs.map(d => (
              <div key={d.id} style={{ fontSize:12, color:'#E24B4A', marginBottom:6 }}>
                📄 {d.file_name}
                {d.qc_correction_notes && <div style={{ fontSize:11, color:'#C0392B', marginTop:3, fontStyle:'italic' }}>📝 {d.qc_correction_notes}</div>}
              </div>
            ))}
          </div>
        )}

        {/* EMPTY STATE */}
        {!loading && scanBacks.length === 0 && pendingDocs.length === 0 && rejectedDocs.length === 0 && signingPkg.length > 0 && (
          <div style={{ fontSize:13, color:'#A2A6AA', textAlign:'center', padding:'14px 0', fontStyle:'italic' }}>
            📄 Scan-backs will appear here once your notary uploads them after the signing!!
          </div>
        )}
      </div>
    </div>
  )
}

// ── ORDER NOTES ───────────────────────────────────────────────
function OrderNotesFull({ jobId, initialNotes }) {
  const [notes,  setNotes]  = useState(initialNotes || '')
  const [saved,  setSaved]  = useState(!!initialNotes)
  const [saving, setSaving] = useState(false)

  async function saveNotes() {
    setSaving(true)
    try {
      await supabase.from('jobs').update({ order_notes: notes }).eq('id', jobId)
      setSaved(true)
      toast.success('Notes saved!! Our team and your notary will see them!!')
    } catch (err) { toast.error(err.message) }
    finally { setSaving(false) }
  }

  return (
    <div className="detail-card">
      <div className="detail-card-header">💬 Order Notes & Special Instructions</div>
      <div style={{ padding:'16px 20px' }}>
        <textarea
          value={notes}
          onChange={e => { setNotes(e.target.value); setSaved(false) }}
          placeholder="Add special instructions for your notary (e.g. Signer needs a Spanish speaker, access code is #1234, please call 30 minutes before arrival)..."
          rows={4}
          style={{ width:'100%', padding:'12px 14px', border:'1.5px solid #D7DADE', borderRadius:10, fontSize:13, resize:'vertical', fontFamily:'inherit', marginBottom:10 }}
        />
        <button onClick={saveNotes} disabled={saving || saved}
          style={{ background:'linear-gradient(135deg,#4D4E8D,#41427a)', color:'#fff', border:'none', borderRadius:8, padding:'10px 20px', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'inherit', opacity: saving || saved ? 0.6 : 1 }}>
          {saving ? 'Saving...' : saved ? '✓ Saved' : '💾 Save Notes'}
        </button>
        <div style={{ fontSize:11, color:'#A2A6AA', marginTop:8 }}>NSV team and your assigned notary will see these notes!!</div>
      </div>
    </div>
  )
}

// ── MAIN PAGE ─────────────────────────────────────────────────
export default function ClientJobDetail() {
  const { id: jobId } = useParams()
  const navigate  = useNavigate()
  const { user }  = useAuth()
  const [job,     setJob]     = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!jobId) { setLoading(false); return }
    loadJob()

    // Real-time subscription for this specific job
    const ch = supabase.channel(`client-job-detail-${jobId}`)
      .on('postgres_changes', {
        event:  'UPDATE',
        schema: 'public',
        table:  'jobs',
        filter: `id=eq.${jobId}`
      }, payload => {
        setJob(prev => ({ ...prev, ...payload.new }))
        toast.success('🔄 Order updated in real time!!')
      })
      .subscribe()
    return () => { supabase.removeChannel(ch) }
  }, [user, jobId])

  async function loadJob() {
    setLoading(true)
    try {
      const { data, error } = await supabase.from('jobs').select('*').eq('id', jobId).maybeSingle()
      if (error) throw error
      if (!data) { setJob(null); return }
      // Security — confirm this user owns the order (client OR individual booking)
      if (user?.id && data.client_id !== user.id && data.individual_user_id !== user.id) {
        toast.error('Access denied — this is not your order!!')
        navigate('/client/orders')
        return
      }
      setJob(data)
    } catch (err) {
      console.error('Order load failed:', err)
      setJob(null)
      toast.error('Could not load this order — please try again.')
    } finally {
      setLoading(false)
    }
  }

  if (loading) return (
    <main style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:300 }}>
      <div className="spinner" />
    </main>
  )

  if (!job) return (
    <main style={{ padding:'48px 20px', textAlign:'center', maxWidth:480, margin:'0 auto' }}>
      <div style={{ fontSize:44, marginBottom:14 }}>🔍</div>
      <h2 style={{ fontFamily:'Plus Jakarta Sans,sans-serif', fontSize:24, color:'#151715', marginBottom:8 }}>We couldn't load this order</h2>
      <p style={{ fontSize:14, color:'#5C6065', marginBottom:24 }}>It may have been removed, or there was a brief connection hiccup. Your order list is safe.</p>
      <button className="btn btn-primary" onClick={() => navigate('/client/orders')} style={{ minHeight:48, padding:'0 24px', borderRadius:10, fontWeight:600 }}>
        ← Back to My Orders
      </button>
    </main>
  )

  const isCompleted = ['completed','awaiting_review','paid','closed'].includes(job.status)
  const cfg = STATUS_CONFIG[job.status] || STATUS_CONFIG.new

  return (
    <>
      <style>{DETAIL_STYLES}</style>
      <a href="#detail-main" className="lca-skip">Skip to main content</a>
      <main id="detail-main" tabIndex={-1}>

        {/* ── BACK BUTTON ── */}
        <BackToOrders />

        {/* ── HERO ── */}
        <div style={{ background:'linear-gradient(135deg,#4D4E8D,#41427a)', borderRadius:16, padding:'24px 28px', marginBottom:16, color:'#fff' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:16, marginBottom:16 }}>
            <div style={{ flex:1, minWidth:200 }}>
              <div style={{ fontSize:11, color:'rgba(255,255,255,0.4)', letterSpacing:'0.12em', marginBottom:4 }}>ORDER NUMBER</div>
              <div style={{ fontFamily:'Plus Jakarta Sans,sans-serif', fontSize:28, color:'#fff', fontWeight:700, lineHeight:1, marginBottom:8 }}>{job.job_number}</div>
              <div style={{ fontSize:15, color:'#fff', fontWeight:500, marginBottom:4 }}>{job.borrower_name || 'Borrower TBD'}</div>
              {Array.isArray(job.additional_signers) && job.additional_signers.filter(s => (s.name||'').trim()).map((s, i) => (
                <div key={i} style={{ marginBottom:4 }}>
                  <span style={{ fontSize:15, color:'#fff', fontWeight:500 }}>{s.name}</span>
                  {s.separate_address && (s.address || s.city) && (
                    <span style={{ fontSize:11, color:'rgba(255,255,255,0.6)', marginLeft:8 }}>📦 ships to {[s.address, s.city, s.state, s.zip].filter(Boolean).join(', ')}</span>
                  )}
                </div>
              ))}
              <div style={{ fontSize:13, color:'rgba(255,255,255,0.65)' }}>{job.signing_type}</div>
              {job.client_po && <div style={{ fontSize:11, color:'rgba(255,255,255,0.4)', marginTop:4 }}>📁 File # {job.client_po}</div>}
            </div>
            <div style={{ flexShrink:0 }}>
              <StatusBadgeLarge status={job.status} />
            </div>
          </div>
        </div>

        {/* ── PAYMENT BANNER (when due) ── */}
        {job.status === 'awaiting_review' && (
          <div className="pay-banner">
            <div>
              <div style={{ fontSize:11, color:'rgba(255,255,255,0.7)', letterSpacing:'0.08em', marginBottom:4 }}>PAYMENT DUE</div>
              <div style={{ fontSize:24, fontWeight:700 }}>${job.total_fee}</div>
              <div style={{ fontSize:12, color:'rgba(255,255,255,0.7)' }}>Your signing has been completed and is ready for payment!!</div>
            </div>
            <a href={`${STRIPE_PAYMENT_LINK}?prefilled_email=${encodeURIComponent(job.borrower_email||'')}&client_reference_id=${job.job_number}`}
              target="_blank" rel="noreferrer"
              style={{ background:'#fff', color:'#635BFF', fontSize:14, fontWeight:700, padding:'12px 24px', borderRadius:10, textDecoration:'none', whiteSpace:'nowrap' }}>
              💳 Pay Securely Now →
            </a>
          </div>
        )}

        {/* ── STATUS TIMELINE ── */}
        <div className="detail-card">
          <div className="detail-card-header">📅 Order Progress</div>
          <FullStatusTimeline status={job.status} />
        </div>

        {/* ── PACKAGE DOWNLOADED MARKER ── */}
        {job.docs_downloaded_at && (
          <div className="detail-card" style={{ borderLeft:'3px solid #5E8A72', background:'#F0FAF5' }}>
            <div style={{ padding:'14px 18px', fontSize:13, color:'#1A4A2E', fontWeight:500 }}>
              📥 Your notary downloaded the signing package · {new Date(job.docs_downloaded_at).toLocaleString()}
            </div>
          </div>
        )}

        {/* ── ORDER INFO + NOTARY (side by side) ── */}
        <div className="detail-grid-2">
          <div className="detail-card">
            <div className="detail-card-header">📋 Order Details</div>
            <div className="detail-info-row" style={{ gridTemplateColumns:'1fr' }}>
              <div className="detail-info-cell">
                <div className="detail-label">SERVICE</div>
                <div className="detail-value">{job.signing_type}</div>
              </div>
              <div className="detail-info-cell">
                <div className="detail-label">SIGNING ADDRESS</div>
                <div className="detail-value">{job.signing_address || '—'}</div>
                <div style={{ fontSize:12, color:'#5C6065', marginTop:2 }}>{job.signing_city}, {job.signing_state} {job.signing_zip}</div>
              </div>
              <div className="detail-info-cell">
                <div className="detail-label">APPOINTMENT</div>
                <div className="detail-value">{job.scheduled_at ? new Date(job.scheduled_at).toLocaleString() : 'TBD — Flexible'}</div>
              </div>
              <div className="detail-info-cell">
                <div className="detail-label">AMOUNT (ALL-INCLUSIVE)</div>
                <div className="detail-value" style={{ color:'#5E8A72', fontWeight:700, fontSize:16 }}>${job.total_fee}</div>
              </div>
              {job.borrower_phone && (
                <div className="detail-info-cell">
                  <div className="detail-label">BORROWER PHONE</div>
                  <div className="detail-value">{job.borrower_phone}</div>
                </div>
              )}
              {job.borrower_email && (
                <div className="detail-info-cell">
                  <div className="detail-label">BORROWER EMAIL</div>
                  <div className="detail-value" style={{ fontSize:12 }}>{job.borrower_email}</div>
                </div>
              )}
            </div>
          </div>

          {/* NOTARY CARD */}
          <NotaryProfileCardFull notaryId={job.notary_id} />
        </div>

        {/* ── LIVE PACKAGE TRACKING ── */}
        {job.tracking_number && <LiveTrackingFull trackingNumber={job.tracking_number} carrier={job.shipping_carrier} />}
        {job.receipt_url && (
          <a href={job.receipt_url} target="_blank" rel="noreferrer" style={{ display:'flex', alignItems:'center', gap:8, padding:'12px 16px', marginTop:10, background:'#F0FAF5', border:'1px solid #B8E0C8', borderRadius:10, fontSize:13, color:'#1A4A2E', fontWeight:600, textDecoration:'none' }}>
            🧾 View shipping receipt →
          </a>
        )}

        {/* ── DOCUMENT VAULT ── */}
        <DocumentVaultFull jobId={job.id} jobStatus={job.status} />

        {/* ── ORDER NOTES ── */}
        <OrderNotesFull jobId={job.id} initialNotes={job.order_notes} />

        {/* ── EMBEDDED MESSAGES ── */}
        <div className="detail-card">
          <div className="detail-card-header">💬 Messages with NSV Team</div>
          <div style={{ padding:'8px' }}>
            <JobMessages jobId={job.id} jobNumber={job.job_number} role="client" />
          </div>
        </div>

        {/* ── INTERNAL RATING (only after completion) ── */}
        {isCompleted && job.notary_id && (
          <StarRatingFull jobId={job.id} notaryId={job.notary_id} isCompleted={isCompleted} />
        )}

        {/* ── BOTTOM BACK BUTTON ── */}
        <div style={{ marginTop:24, textAlign:'center' }}>
          <BackToOrders />
        </div>

      </main>
    </>
  )
}
